import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, Clock, MapPin, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/lib/motion";
import { CONTACT } from "@/data/content";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Website enquiry from ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || "Not provided"}\n\n${form.message}`,
    );
    window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
    toast.success("Your email app has been opened with your message ready to send.");
  };

  const inputClass = "rounded-xl bg-background border-border h-12 focus-visible:ring-primary";
  const wa = `https://wa.me/${CONTACT.whatsapp}`;

  const details = [
    { icon: Phone, label: "Landline", value: CONTACT.landline, href: `tel:${CONTACT.landline.replace(/\s/g, "")}` },
    { icon: Phone, label: "Mobile", value: CONTACT.mobile, href: `tel:${CONTACT.mobile.replace(/\s/g, "")}` },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: MessageCircle, label: "WhatsApp", value: "Click to chat", href: wa },
    { icon: MapPin, label: "Address", value: CONTACT.address },
    { icon: MapPin, label: "Service Area", value: CONTACT.area },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Get In Touch" title="Contact us"
        subtitle="Questions about a treatment or ready to book? We'd love to hear from you." />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12">
        <Reveal>
          <div className="space-y-6">
            {details.map((d) => (
              <a key={d.label} href={d.href} target={d.href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                data-testid={`contact-info-${d.label.toLowerCase().replace(" ", "-")}`}
                className={`flex items-start gap-4 rounded-2xl border border-border bg-card p-5 ${d.href ? "hover:border-primary hover:-translate-y-0.5 transition-all" : ""}`}>
                <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><d.icon size={20} /></div>
                <div>
                  <p className="text-xs tracking-[0.15em] uppercase text-foreground/50">{d.label}</p>
                  <p className="text-lg text-foreground font-medium">{d.value}</p>
                </div>
              </a>
            ))}
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center gap-3 mb-4"><Clock size={20} className="text-primary" /><p className="text-xs tracking-[0.15em] uppercase text-foreground/50">Business Hours</p></div>
              <ul className="space-y-2">
                {CONTACT.hours.map((h) => (
                  <li key={h.day} className="flex justify-between text-sm"><span className="text-muted-foreground">{h.day}</span><span className="text-foreground font-medium">{h.time}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-border bg-card p-7 sm:p-10 space-y-6">
            <h2 className="font-serif text-3xl text-primary">Send a message</h2>
            <div><Label className="mb-2 block text-sm font-semibold">Name</Label><Input data-testid="contact-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputClass} /></div>
            <div><Label className="mb-2 block text-sm font-semibold">Email</Label><Input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputClass} /></div>
            <div><Label className="mb-2 block text-sm font-semibold">Phone (optional)</Label><Input data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputClass} /></div>
            <div><Label className="mb-2 block text-sm font-semibold">Message</Label><Textarea data-testid="contact-message" required value={form.message} onChange={(e) => set("message", e.target.value)} className="rounded-xl bg-background border-border min-h-32 focus-visible:ring-primary" /></div>
            <button type="submit" data-testid="contact-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-8 py-4 font-semibold hover:bg-primary/90 transition-colors">
              Send Message <ArrowUpRight size={18} />
            </button>
          </form>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="rounded-3xl overflow-hidden border border-border h-[420px]">
            <iframe title="Risi Wellness service area — Manchester" data-testid="google-map"
              src="https://www.google.com/maps?q=Bartle+House+9+Oxford+Court+Manchester+M2+3WQ&z=15&output=embed"
              className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;
